import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Users, Building2, GraduationCap, FileBadge, AlertTriangle,
  ScrollText, Shield, KeyRound, LogOut, Activity, ShieldCheck, ShieldAlert,
  CreditCard, Tag, Receipt, Plug, SlidersHorizontal, Megaphone, FileText,
  Moon, Sun, UserCog, HardDrive, MessageSquare, Award,
} from "lucide-react";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/organizations", label: "Organizations", icon: Building2 },
  { to: "/admin/institutions", label: "Institutions", icon: GraduationCap },
  { to: "/admin/certificates", label: "Certificates", icon: FileBadge },
  { to: "/admin/disputes", label: "Disputes", icon: AlertTriangle },
  { to: "/admin/trust-safety", label: "Trust & Safety", icon: ShieldAlert },
  { to: "/admin/trust-safety/alerts", label: "Alert Queue", icon: ShieldAlert, nested: true },
  { to: "/admin/plans", label: "Plans", icon: CreditCard },
  { to: "/admin/coupons", label: "Coupons", icon: Tag },
  { to: "/admin/payments", label: "Payments", icon: Receipt },
  { to: "/admin/api-portal", label: "API & Webhooks", icon: Plug },
  { to: "/admin/partners", label: "Partner API", icon: Plug },
  { to: "/admin/platform", label: "Platform Config", icon: SlidersHorizontal },
  { to: "/admin/communications", label: "Communications", icon: Megaphone },
  { to: "/admin/cms", label: "CMS", icon: FileText },
  { to: "/admin/inquiries", label: "Inquiries · Demos", icon: MessageSquare },
  { to: "/admin/awards", label: "Institution Awards", icon: Award },
  { to: "/admin/staff", label: "Staff & Roles", icon: UserCog },
  { to: "/admin/ops", label: "Ops · Backup · AI · DSAR", icon: HardDrive },
  { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText },
  { to: "/admin/security", label: "Security", icon: Shield },
  { to: "/admin/mfa", label: "MFA Settings", icon: KeyRound },
];

export default function AdminLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [mfa, setMfa] = useState(null);
  useEffect(() => {
    api.get("/admin/mfa/status").then((r) => setMfa(r.data)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-ec-ink text-white" data-testid="admin-layout">
      <header className="bg-black/30 border-b border-white/10 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 h-14 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2.5" data-testid="admin-logo">
            <div className="w-7 h-7 bg-ec-emerald rounded-sm flex items-center justify-center"><ShieldCheck className="w-4 h-4 text-ec-ink" /></div>
            <div className="font-display font-black text-lg tracking-tight">EthicCred · Admin</div>
            <span className="eyebrow text-ec-emerald ml-2 hidden sm:inline">Master Control</span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden sm:flex items-center gap-2 text-zinc-300">
              <Activity className="w-3.5 h-3.5 text-ec-emerald" />
              <span className="font-mono-plex text-xs">{user?.email}</span>
            </span>
            {mfa && (
              <span className={`text-[10px] font-mono-plex px-2 py-1 rounded-sm border ${mfa.mfa_enabled ? "border-ec-emerald/50 text-ec-emerald bg-ec-emerald/10" : "border-amber-500/50 text-amber-400 bg-amber-500/10"}`} data-testid="admin-mfa-pill">
                MFA · {mfa.mfa_enabled ? "ON" : "OFF"}
              </span>
            )}
            <button onClick={toggle} className="text-zinc-400 hover:text-white p-1.5" aria-label="Toggle theme" data-testid="admin-theme-toggle" title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}>
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button
              onClick={() => { logout(); window.location.href = "/adentry"; }}
              className="text-zinc-400 hover:text-white p-1.5"
              aria-label="Sign out"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-8 grid lg:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden lg:block" data-testid="admin-sidebar">
          <div className="eyebrow text-zinc-500 mb-4">Modules</div>
          <nav className="space-y-0.5">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm border-l-2 transition-colors ${item.nested ? "ml-4" : ""} ${isActive ? "border-ec-emerald bg-ec-emerald/10 font-semibold text-white" : "border-transparent text-zinc-400 hover:text-white hover:bg-white/5"}`
                  }
                  data-testid={`admin-sidebar-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="mt-8 p-4 border border-white/10 rounded-sm text-xs text-zinc-500 leading-relaxed font-mono-plex">
            Every action you take here is logged with your identity, IP, and timestamp. Use only what's necessary.
          </div>
        </aside>

        <main className="min-w-0">
          {(title || actions) && (
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                {subtitle && <div className="eyebrow text-ec-emerald">{subtitle}</div>}
                {title && <h1 className="font-display text-4xl font-black tracking-tight mt-2">{title}</h1>}
              </div>
              {actions}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
